import { lazy, Suspense, useEffect, useMemo } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress, Stack, Typography, Alert, ThemeProvider, CssBaseline } from '@mui/material';
import Layout from './components/Layout';
import Onboarding from './components/Onboarding';
import AuthGate, { requiresAuthGate, WebLinkPrompt } from './components/AuthGate';
import UpgradePrompt from './components/UpgradeDialog';
import { useAuth } from './auth/auth';
import { isTauri } from './ai/tauri';
import { cloudConfig, isHostedWeb } from './config/cloud';
import { getTheme } from './theme';
import { useAppStore } from './store/useAppStore';

// Route-level code splitting: keeps heavy screens (charts, document tooling)
// out of the first-paint bundle. The Dashboard stays eager — it's the landing
// route and lazy-loading it would just add a flash.
import Dashboard from './screens/Dashboard';
const StudentProfileScreen = lazy(() => import('./screens/StudentProfile'));
const DentalPrograms = lazy(() => import('./screens/DentalPrograms'));
const SchoolDossierScreen = lazy(() => import('./screens/SchoolDossier'));
const ProgramResearch = lazy(() => import('./screens/ProgramResearch'));
const ResearchReview = lazy(() => import('./screens/ResearchReview'));
const QuestionBank = lazy(() => import('./screens/QuestionBank'));
const SavedQuestions = lazy(() => import('./screens/SavedQuestions'));
const AnswerBuilder = lazy(() => import('./screens/AnswerBuilder'));
const AnswerLibrary = lazy(() => import('./screens/AnswerLibrary'));
const InfinitePractice = lazy(() => import('./screens/InfinitePractice'));
const MMIStations = lazy(() => import('./screens/MMIStations'));
const MockInterview = lazy(() => import('./screens/MockInterview'));
const RedFlagCoach = lazy(() => import('./screens/RedFlagCoach'));
const InterviewDay = lazy(() => import('./screens/InterviewDay'));
const PrepPlan = lazy(() => import('./screens/PrepPlan'));
const Analytics = lazy(() => import('./screens/Analytics'));
const Calibration = lazy(() => import('./screens/Calibration'));
const PromptRubric = lazy(() => import('./screens/PromptRubric'));
const Settings = lazy(() => import('./screens/Settings'));

function RouteFallback() {
  return (
    <Stack alignItems="center" justifyContent="center" sx={{ py: 10 }} spacing={2}>
      <CircularProgress size={28} />
    </Stack>
  );
}

export default function App() {
  const ready = useAppStore((s) => s.ready);
  const initError = useAppStore((s) => s.initError);
  const init = useAppStore((s) => s.init);
  const settings = useAppStore((s) => s.settings);
  const auth = useAuth();

  useEffect(() => {
    void init();
  }, [init]);

  const theme = useMemo(
    () => getTheme(settings.themeMode, settings.themePalette),
    [settings.themeMode, settings.themePalette],
  );

  // Keep the page background in lockstep with the theme. boot.css paints a
  // light pre-React fallback on html/body; an inline style is the one thing
  // guaranteed to outrank it, so dark mode can never be stuck on white.
  useEffect(() => {
    const bg = theme.palette.background.default;
    document.documentElement.style.backgroundColor = bg;
    document.body.style.backgroundColor = bg;
  }, [theme]);

  let content: JSX.Element;
  if (initError) {
    content = (
      <Box sx={{ p: 6, maxWidth: 640, mx: 'auto' }}>
        <Alert severity="error">
          <Typography variant="subtitle1">Failed to initialize the local database</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {initError}
          </Typography>
        </Alert>
      </Box>
    );
  } else if (!ready) {
    content = (
      <Stack alignItems="center" justifyContent="center" sx={{ height: '100vh' }} spacing={2}>
        <CircularProgress />
        <Typography color="text.secondary">Loading your local interview workspace…</Typography>
      </Stack>
    );
  } else if (requiresAuthGate(isTauri(), cloudConfig.syncEnabled, auth.state)) {
    // Hosted web portal (cloud-enabled browser build): everything is locked
    // behind sign-in. The desktop build and local browser builds without
    // cloud config are local-first and never gate.
    content = <AuthGate />;
  } else if (!settings.onboardedAt) {
    // First run: guided setup (Codex check, materials, first school, first research).
    content = <Onboarding />;
  } else {
    content = (
      <Layout>
        {!isTauri() && <WebLinkPrompt />}
        {cloudConfig.syncEnabled && <UpgradePrompt />}
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/profile" element={<StudentProfileScreen />} />
          <Route path="/red-flags" element={<RedFlagCoach />} />
          <Route path="/programs" element={<DentalPrograms />} />
          <Route path="/dossier/:programId" element={<SchoolDossierScreen />} />
          <Route path="/research" element={<ProgramResearch />} />
          <Route path="/review" element={<ResearchReview />} />
          <Route path="/questions" element={<QuestionBank />} />
          <Route path="/saved" element={<SavedQuestions />} />
          <Route path="/answers" element={<AnswerBuilder />} />
          <Route path="/answers/:questionId" element={<AnswerBuilder />} />
          <Route path="/answer-library" element={<AnswerLibrary />} />
          <Route path="/practice" element={<InfinitePractice />} />
          <Route path="/mmi" element={<MMIStations />} />
          <Route path="/mock" element={<MockInterview />} />
          <Route path="/interview-day" element={<InterviewDay />} />
          <Route path="/plan" element={<PrepPlan />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/calibration" element={<Calibration />} />
          {/* Prompt engineering is a desktop/dev tool — hidden on the hosted portal. */}
          <Route path="/prompts" element={isHostedWeb() ? <Navigate to="/" replace /> : <PromptRubric />} />
          <Route path="/settings" element={<Settings />} />
        </Routes>
        </Suspense>
      </Layout>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {content}
    </ThemeProvider>
  );
}
